import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pfe_token') || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      if (token) {
        try {
          const profile = await api.get('/auth/profile');
          // profile is now already extracted from the nested structure
          setUser(profile);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const userData = await api.post('/auth/login', { email, password });
      console.log('Login response (auto-extracted):', userData);
      
      // userData is now already extracted from the nested structure
      const { accessToken, ...userInfo } = userData;
      
      setToken(accessToken);
      setUser(userInfo);
      localStorage.setItem('pfe_token', accessToken);
      
      return { success: true, user: userInfo };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const logout = () => {
    setUser(null);
    setToken('');
    localStorage.removeItem('pfe_token');
    localStorage.removeItem('remoteAttendanceSession');
  };

  const clearAuth = () => {
    console.log('Clearing authentication...');
    setUser(null);
    setToken('');
    localStorage.removeItem('pfe_token');
    localStorage.removeItem('remoteAttendanceSession');
  };

  const isAuthenticated = () => !!token && !!user;
  const isAdmin = () => user?.role?.toLowerCase() === 'admin';
  const isManager = () => user?.role?.toLowerCase() === 'manager';
  const isAdminOrManager = () => isAdmin() || isManager();

  const value = {
    user,
    token,
    login,
    logout,
    clearAuth,
    isAuthenticated,
    isAdmin,
    isManager,
    isAdminOrManager,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
